import { createEvent } from "ics";
import { size } from "lodash";
import { saveAs } from "file-saver";
import { toast } from "../../components/ui/Toast/useToast";
import { SERVICE_BOOKING_STEPS } from "../../config/constants";
import { getServiceBookingUndoStepFn } from "../../transactions/serviceBooking/newServiceBooking";

export const buildGaragesCalendar = (
  bookingSelectOptions = [],
  bookingCalendarOptions = []
) => {
  const garagesMap = new Map();

  bookingSelectOptions.forEach((garage, index) => {
    garagesMap.set(garage.garageId || index, {
      garageName: garage?.garageName,
      address: garage?.address,
      coordinates: garage?.coordinates,
      cfmsCode: garage?.cfmsCode,
      county: garage?.county,
      distanceFromUser: garage?.distanceFromUser,
      garageId: garage?.garageId,
      make: garage?.make,
      province: garage?.province,
      type: garage?.type,
      calendarData: [],
    });
  });

  bookingCalendarOptions.forEach(slot => {
    const { garageName, startTime, endTime } = slot;

    // optimized, but slots don't come with id, they come with NAME
    // if (!garagesMap.has(garageName)) {
    //   // safety net ( backend inconsistency)
    //   garagesMap.set(garageName, {
    //     garageName,
    //     calendarData: [],
    //   });
    // }

    // garagesMap.get(garageName).calendarData.push({
    //   startTime: startTime,
    //   endTime: endTime,
    // });

    // so we have to run every slot -> for every garage: if slot garage name === garage.garageName
    for (const garage of garagesMap.values()) {
      if (garage.garageName === garageName) {
        garage.calendarData.push({
          startTime,
          endTime,
        });
      }
    }

  });

  return Array.from(garagesMap.values());
};

export const toUTCISOString = (date, time) => {
  const local = new Date(
    new Date(`${date}T${time}:00`).toLocaleString("en-US", {
      timeZone: "Europe/Athens",
    })
  );
  return local.toISOString().replace(".000", "");
};

export const getTimeslotWindow = ({slot, dateKey}) => {
    // Convert backend slots → slot windows
    const start = new Date(slot?.startTime);
    const end = new Date(slot?.endTime);
    return {
        date: dateKey,
        start: start?.toLocaleTimeString("el-GR", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        }),
        end: end?.toLocaleTimeString("el-GR", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        }),
    };
  }

export const formatBookingCalendar = (isoString) => {
  const date = new Date(isoString);

  const dayName = date.toLocaleDateString('el-GR', {
    weekday: 'long',
    timeZone: 'Europe/Athens',
  });

  const datePart = date.toLocaleDateString('el-GR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Europe/Athens',
  });

  const timePart = date.toLocaleTimeString('el-GR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Europe/Athens',
  });

  return {day: `${dayName} ${datePart}`, time:`${timePart}`};
};

export const validateResponse = (response) => {
  if (response?.exceptions && size(response.exceptions) > 0) {
    const message = response?.exceptions?.[0]
    || response?.exceptions?.bodyWork
    toast({
      variant: 'destructive',
      description: message || 'Παρουσιάστηκε κάποιο πρόβλημα',
    });
    return false;
  }
  return true;
};

export const addToCalendar = (booking) => {
  const startDate = new Date(booking?.bookingCalendar)

  const event = {
    start: [
      startDate.getUTCFullYear(),
      startDate.getUTCMonth() + 1,
      startDate.getUTCDate(),
      startDate.getUTCHours(),
      startDate.getUTCMinutes(),
    ],
    duration: { hours: 2 },
    title: 'AVIS Service Ραντεβού',
    description: `Όχημα: ${booking.plateno}`,
    location: booking.bookingSelect,
    geo: { lat: booking?.bookingSelectObj?.[0]?.coordinates?.lat, lon: booking?.bookingSelectObj?.[0]?.coordinates?.lng }
  };

  createEvent(event, (error, value) => {
    const blob = new Blob([value], { type: "text/plain;charset=utf-8" });
    saveAs(blob, "event-schedule.ics");
  });
    
};

const isFutureDate = (date) => {
  if (!date) return false;
  return new Date(date).setHours(0,0,0,0) > new Date().setHours(0,0,0,0);
};

const monthsBetween = (from, to) => {
  const start = new Date(from);
  const end = new Date(to);
  return (
    (end.getFullYear() - start.getFullYear()) * 12 +
    (end.getMonth() - start.getMonth())
  );
};

const daysBetween = (from, to) => {
  const start = new Date(from);
  const end = new Date(to);

  const diffInMs = Math.abs(end - start);
  const msInDay = 1000 * 60 * 60 * 24;

  return Math.floor(diffInMs / msInDay);
};

export function computeOptionStatus({
  optionData,
  currentMileage,
  standarizedService=false,
  tyresChange=false,
  bodyWork=false,
}) {
  const {
    mileage,
    date,
    currentlyAllowed,
    currentlyScheduled,
  } = optionData;

  // Scheduled has absolute priority
  if (currentlyScheduled) {
    return {
      status: "scheduled",
      message: `Έχεις ήδη προγραμματισμένο ραντεβού ${standarizedService ? "service" : tyresChange? "αλλαγής ελαστικών" : "φανοποιίας"}.`,
    };
  }

  const mileageRelevant = mileage > 0;
  const dateRelevant = isFutureDate(date);

  const remainingKm = mileageRelevant
    ? Math.max(mileage - currentMileage, 0)
    : null;

  const remainingMonths = dateRelevant
    ? monthsBetween(new Date(), date)
    : null;

  let remainingDays = 0;

  if(remainingMonths === 0) {
    remainingDays = dateRelevant ? daysBetween(new Date(), date) : null
  }
  
  // Date + mileage BOTH relevant
  if (mileageRelevant && dateRelevant) {
    if (currentMileage >= mileage) {
      return {
        status: "eligible",
        message: null,
      };
    }
  
    return {
      status: "lockedUntilFirst",
      remainingKm,
      remainingMonths,
      message: `${standarizedService 
        ? "Δικαιούσαι service"
        : tyresChange 
          ?"Δικαιούσαι αντικατάσταση ελαστικών" 
          : "Δικαιούσαι εργασία φανοποιίας"} σε ${remainingMonths > 0 
            ? remainingMonths 
            : remainingDays} ${parseInt(remainingMonths) > 1 
              ? 'μήνες' 
              : remainingMonths === 0 
                ? remainingDays > 1 
                  ? 'μέρες'
                  : "μέρα" 
                : 'μήνα'} ή ${remainingKm} km. ${standarizedService ? "Συμβουλεύσου τον οδηγό ενδείξεων ή επικοινώνησε με το +302106879800 σε περίπτωση βλάβης." : ""}`,
    };
  }

  // Only mileage relevant
  
  if (mileageRelevant && !dateRelevant) {
    
    if (currentMileage >= mileage) {
      return {
        status: "eligible",
        message: null,
      };
    }
    
    return {
      status: "lockedUntilMileage",
      remainingKm,
      message: `${standarizedService ? 
        "Δικαιούσαι service" 
        : tyresChange ? 
        "Δικαιούσαι αντικατάσταση ελαστικών" 
          : "Δικαιούσαι εργασία φανοποιίας"} σε ${remainingKm} km. ${standarizedService ? "Συμβουλέψου τον οδηγό ενδείξεων ή επικοινώνησε με το 210 6879800 σε περίπτωση βλάβης." : ""}`,
    };
  }

  // Only date relevant (mileage === -1)
  if (!mileageRelevant && dateRelevant) {
    return {
      status: "lockedUntilDate",
      remainingMonths,
      message: `${standarizedService ?
        "Δικαιούσαι service"
        : tyresChange ?
        "Δικαιούσαι αντικατάσταση ελαστικών" 
          : "Δικαιούσαι εργασία φανοποιίας"} σε ${remainingMonths > 0 
            ? remainingMonths 
            : remainingDays} ${parseInt(remainingMonths) > 1 ? 'μήνες' : remainingMonths === 0 
                ? remainingDays > 1 
                  ? 'μέρες'
                  : "μέρα" 
                : 'μήνα'}. ${standarizedService ? "Συμβουλέψου τον οδηγό ενδείξεων ή επικοινώνησε με το 210 6879800 σε περίπτωση βλάβης." : ""}`,
    };
  }

  // Fallback: mileage & date irrelevant
  if (currentlyAllowed) {
    return {
      status: "eligible",
      message: null,
    };
  }

  return {
    status: "notEligible",
    message:  `Δεν δικαιούσαι ${standarizedService ? "τακτικό service." : tyresChange ? "προγραμματισμένη αλλαγή ελαστικών." : "εργασίες φανοποιίας."} Επικοινώνησε με το +302106879800  σε περίπτωση βλάβης.`,
  };
}

export const undoUntil = async ({ 
  targetStep,
  state,
  dispatch,
}) => {
  if (!state?.serviceFlowId) {
    console.warn('undoUntil: missing serviceFlowId');
    return;
  }

  const targetStepStatus = SERVICE_BOOKING_STEPS[targetStep];

  if (!targetStepStatus) {
    return;
  }

  let currentStepStatus = SERVICE_BOOKING_STEPS[state.stepStatus];

  // Nothing to undo
  if (currentStepStatus <= targetStepStatus) {
    return;
  }

  const MAX_UNDOS = 7; // hard safety guard
  let undoCount = 0;

  while (currentStepStatus > targetStepStatus) {

    if (undoCount >= MAX_UNDOS) {
      toast({
        variant: 'destructive',
        description: "Δεν είναι δυνατόν να γίνει αναίρεση περαιτέρω βημάτων.",
      });
      console.error(
        'undoUntil: exceeded max undo attempts',
        { currentStepStatus, targetStepStatus }
      );
      break;
    }

    undoCount += 1;

    let response;

    try {
      response = await getServiceBookingUndoStepFn({
        serviceFlowId: state.serviceFlowId,
        payload: {},
      });
    } catch (error) {
      console.error('undoUntil: undo call failed', error);
      break;
    }

    if(!validateResponse(response)) {
      const message = response?.exceptions?.[0] || 'Δεν είναι δυνατή η αναίρεση του βήματος.';
      toast({
        variant: 'destructive',
        description: message,
      });
      return {success: false, message}
    } else {

      const newStepName = response?.name;
      const newStepStatus = SERVICE_BOOKING_STEPS[newStepName];

      if (!newStepStatus) {

        console.error(
          'undoUntil: BE returned unknown step name',
          newStepName
        );
        break;
      }

      // Safety: BE did not move us backwards
      if (newStepStatus >= currentStepStatus) {

        console.error(
          'undoUntil: undo did not move backwards',
          { currentStepStatus, newStepStatus }
        );
        break;
      }

      currentStepStatus = SERVICE_BOOKING_STEPS[newStepName];

      dispatch({
        type: 'SET_STEPSTATUS',
        stepStatus: newStepName,
      });
    }
  }
  return {success: true}
};

export const clearSBStepStatusState = ({targetStepStatus, dispatch}) => {

  switch (targetStepStatus) {
    case "location":
      dispatch({
        type: "RESET_FIELDS",
        fields: [
          "selectedPickup", 
          "stepTwoAvailableAppointments", 
          "stepThreeOptions", 
          "pickupCost", 
          "stepThree",
          "calendarData",
          'calendarValues',
          'selectedGarage',
          'garageCalendarData', 
          'standarizedService',
          'tyresChange',
          "bodyWork"
        ],
      });
      return;
    case "optionSelection":
      dispatch({
        type: "RESET_FIELDS",
        fields: [
          "calendarData",
          'calendarValues',
          'selectedGarage',
          'garageCalendarData', 
          'standarizedService',
          'tyresChange',
          "bodyWork"
        ],
      });
      dispatch({ type: "RESET_ADDRESS_STREET" });
      return;
    case "pickupDeliveryAddress":
      dispatch({
        type: "RESET_FIELDS",
        fields: [
          "calendarData",
          'calendarValues',
          'selectedGarage',
          'garageCalendarData', 
          'standarizedService',
          'tyresChange',
          "bodyWork"
        ],
      });
      return;
    case "booking":
      dispatch({
        type: "RESET_FIELDS",
        fields: [
          'standarizedService',
          'tyresChange',
          "bodyWork",
        ],
      });
      return;
    case "formForCRM":
      dispatch({
        type: "RESET_FIELDS",
        fields: [
          'standarizedService',
          'tyresChange',
          "bodyWork"
        ],
      });
      return;
    case "tyreChange":
      dispatch({
        type: "RESET_FIELDS",
        fields: ["appointmentSummary"],
      });
      return;
    case "bodyWork":
      dispatch({
        type: "RESET_FIELDS",
        fields: ["appointmentSummary"],
      });
      return;
    default:
      return null;
  }
}

export const getGarageSelectionTitle = ({state}) => {

  const garages = state?.garageCalendarData || [];
  const firstGarage = garages[0];

  if (!firstGarage) {
    return "Δεν βρέθηκαν συνεργεία";
  }
  const keys = Object.keys(firstGarage);

  const hasMultipleChoices = garages.length > 1;
  const isAuthorizedGarage =
    keys.includes("type") && firstGarage.type === "Authorized";

  const isProvinceGarage =
    keys.includes("type") && firstGarage.type === "Province";

  // When user must choose → "Επιλογή εξουσιοδοτημένου συνεργείου"
  if (hasMultipleChoices) {
    return `Επιλογή ${
      isAuthorizedGarage ? "εξουσιοδοτημένου συνεργείου" : isProvinceGarage ? "συνεργαζόμενου συνεργείου" : "συνεργείου Avis"
    }`;
  }

  // When only one → "Εξουσιοδοτημένο συνεργείο"
  return `${
    isAuthorizedGarage ? "Εξουσιοδοτημένο συνεργείο εξυπηρέτησης" : isProvinceGarage ? "Συνεργαζόμενο συνεργείο εξυπηρέτησης" : "Συνεργείο εξυπηρέτησης Avis"
  }`;
};